import { useEffect, useRef, useState } from 'preact/hooks';
import { apiFetch } from '../api/client';
import { AgreementPanel, hasAgreementMetadata } from './AgreementPanel';
import { ReconnectSquare } from './ReconnectSquare';
import './guided-setup.css';
import { ASSISTANT_NAME } from '../brand.js';

const steps = ['Your account', 'Salon agreement', 'Connect Square', 'Choose location', 'Subscription', `Meet ${ASSISTANT_NAME}`];
export function GuidedSetup({ step, children }) {
  return <main class="guided-setup">
    <header class="setup-header"><a href="/" class="setup-brand">{ASSISTANT_NAME}<span>Your salon, thoughtfully connected.</span></a><a href="mailto:support@gojonathan.ai">Get help</a></header>
    <div class="setup-layout"><aside class="setup-roadmap"><p class="setup-eyebrow">LET’S GET YOU SET UP</p><h1>A little setup.<br />A lot more support.</h1><p>Bring your salon to {ASSISTANT_NAME}, one step at a time.</p><ol aria-label="Setup progress">{steps.map((label, index) => <li aria-current={index === step ? 'step' : undefined} class={index < step ? 'done' : ''}><span aria-hidden="true">{index < step ? '✓' : index + 1}</span>{label}</li>)}</ol></aside>
      <div class="setup-main"><p class="setup-mobile-step">Step {step + 1} of {steps.length} · {steps[step]}</p><div class="setup-card">{children}</div><p class="setup-footnote">Your progress stays with your account. You can come back anytime.</p></div>
    </div>
  </main>;
}

export function AgreementStep({ onUpdated }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const pending = useRef(false);
  const alive = useRef(false);
  async function load(updated = false) {
    if (pending.current) return;
    pending.current = true;
    setLoading(true); setError('');
    try {
      const result = await apiFetch('/v1/owner/billing');
      if (!alive.current) return;
      setData(result);
      if (updated || result?.agreement?.accepted === true) onUpdated?.();
    } catch (err) { if (alive.current) setError(err.message || 'Could not load your salon agreement.'); }
    finally { pending.current = false; if (alive.current) setLoading(false); }
  }
  useEffect(() => { alive.current = true; load(); return () => { alive.current = false; }; }, []);
  return <><p class="setup-eyebrow">WELCOME ABOARD</p><h2>First, make it official.</h2><p>Review how {ASSISTANT_NAME} works with your salon. Next, we’ll connect Square so you can bring over your services and team.</p>
    {loading && <p role="status">Loading your salon agreement…</p>}
    {error && <><p role="alert">{error}</p><button class="setup-primary" disabled={loading} onClick={() => load()}>Try again</button></>}
    {data && !hasAgreementMetadata(data.agreement) && !error && <button class="setup-primary" disabled={loading} onClick={() => load()}>Reload agreement</button>}
    {data?.agreement?.accepted === true && <button class="setup-primary" onClick={onUpdated}>Continue to Square</button>}
    {data && <AgreementPanel agreement={data.agreement} internal={data.funding_source === 'internal'} disabled={loading} onReload={() => load(true)} />}
  </>;
}

const returnMessages = {
  expired: 'That Square link expired. Start a fresh connection below.',
  cancelled: 'Square connection was cancelled. You can try again when you’re ready.',
  unavailable: 'We couldn’t confirm the Square connection. Try again, or check whether it has finished.',
  mismatch: 'That Square business doesn’t match this salon’s connection. Sign in to the Square business you connected before.',
  failed: 'The Square connection didn’t finish. Let’s try again.',
};
export function squareReturnFailure(value) { return Object.hasOwn(returnMessages, value); }
export function SquareRecovery({ hint, onRecovered }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const pending = useRef(false);
  const callback = useRef(onRecovered); callback.current = onRecovered;
  const alive = useRef(false);
  async function check() {
    if (pending.current) return;
    pending.current = true; setBusy(true); setError('');
    try {
      const result = await apiFetch('/v1/owner/square/locations');
      if (!Array.isArray(result?.locations)) throw new Error('Your Square connection is not confirmed yet.');
      if (alive.current) callback.current();
    } catch (err) { if (alive.current) setError(err.message || 'Your Square connection is not confirmed yet.'); }
    finally { pending.current = false; if (alive.current) setBusy(false); }
  }
  useEffect(() => {
    alive.current = true;
    window.addEventListener('focus', check); window.addEventListener('pageshow', check);
    return () => { alive.current = false; window.removeEventListener('focus', check); window.removeEventListener('pageshow', check); };
  }, []);
  return <><p class="setup-eyebrow">LET’S PICK UP WHERE YOU LEFT OFF</p><h2>Reconnect with Square.</h2><p role="status">{returnMessages[hint]}</p><ReconnectSquare guided />
    <button class="setup-text-button" disabled={busy} onClick={check}>{busy ? 'Checking connection…' : 'Already finished in Square? Check connection'}</button>
    {error && <p role="alert">{error}</p>}
  </>;
}
